(() => {
  document.documentElement.classList.add('skinny-ready');

  const padNumber = (value) => String(value).padStart(2, '0');

  const initCarousel = (carousel) => {
    if (carousel.dataset.shCarouselInitialized === 'true') return;
    carousel.dataset.shCarouselInitialized = 'true';
    const viewport = carousel.querySelector('[data-sh-carousel-viewport]');
    const track = carousel.querySelector('[data-sh-carousel-track]');
    const slides = [...track.children];
    const previous = carousel.querySelector('[data-sh-carousel-prev]');
    const next = carousel.querySelector('[data-sh-carousel-next]');
    const status = carousel.querySelector('[data-sh-carousel-status]');
    const toggle = carousel.querySelector('[data-sh-carousel-toggle]');
    const progress = carousel.querySelector('[data-sh-carousel-progress]');
    const railButtons = [...carousel.querySelectorAll('[data-sh-carousel-go]')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayDelay = parseFloat(getComputedStyle(carousel).getPropertyValue('--sh-autoplay-duration')) || 5000;
    let index = 0;
    let pointerStart = null;
    let autoplayTimer = null;
    let manuallyPaused = reducedMotion.matches;
    let hoverPaused = false;
    let focusPaused = false;
    let pointerPaused = false;
    let outOfView = true;

    if (slides.length < 2) {
      carousel.dataset.autoplay = 'paused';
      carousel.querySelector('.sh-product-slider__controls')?.setAttribute('hidden', '');
      return;
    }

    const isPaused = () => manuallyPaused || hoverPaused || focusPaused || pointerPaused || outOfView || document.hidden;

    const updateAutoplayUI = () => {
      const paused = isPaused();
      carousel.dataset.autoplay = paused ? 'paused' : 'running';
      toggle.setAttribute('aria-label', manuallyPaused ? 'Start automatic product views' : 'Pause automatic product views');
      toggle.setAttribute('aria-pressed', String(manuallyPaused));
      status.setAttribute('aria-live', manuallyPaused ? 'polite' : 'off');
    };

    const restartProgress = () => {
      progress.style.animation = 'none';
      void progress.offsetWidth;
      progress.style.animation = '';
    };

    const warmAdjacentSlides = () => {
      const adjacentIndexes = [
        (index - 1 + slides.length) % slides.length,
        (index + 1) % slides.length,
      ];
      adjacentIndexes.forEach((slideIndex) => {
        const image = slides[slideIndex]?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decode?.().catch(() => {});
      });
    };

    const render = (instant = false) => {
      if (instant) track.style.transition = 'none';
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      status.textContent = `${padNumber(index + 1)} / ${padNumber(slides.length)}`;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.tabIndex = active ? 0 : -1;
      });
      railButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.toggleAttribute('aria-current', active);
      });
      warmAdjacentSlides();
      if (instant) requestAnimationFrame(() => { track.style.transition = ''; });
    };

    const move = (step) => {
      index = (index + step + slides.length) % slides.length;
      render();
    };

    const scheduleAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      updateAutoplayUI();
      if (isPaused()) return;
      restartProgress();
      autoplayTimer = window.setTimeout(() => {
        move(1);
        scheduleAutoplay();
      }, autoplayDelay);
    };

    previous.addEventListener('click', () => { move(-1); scheduleAutoplay(); });
    next.addEventListener('click', () => { move(1); scheduleAutoplay(); });
    railButtons.forEach((button) => {
      button.addEventListener('click', () => {
        index = Number(button.dataset.shCarouselGo) || 0;
        render();
        scheduleAutoplay();
      });
    });
    toggle.addEventListener('click', () => {
      manuallyPaused = !manuallyPaused;
      scheduleAutoplay();
    });
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
        scheduleAutoplay();
      }
    });
    viewport.addEventListener('pointerdown', (event) => {
      pointerPaused = true;
      pointerStart = { x: event.clientX, y: event.clientY };
      scheduleAutoplay();
    });
    viewport.addEventListener('pointerup', (event) => {
      if (pointerStart) {
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) move(deltaX < 0 ? 1 : -1);
      }
      pointerStart = null;
      pointerPaused = false;
      scheduleAutoplay();
    });
    viewport.addEventListener('pointercancel', () => {
      pointerStart = null;
      pointerPaused = false;
      scheduleAutoplay();
    });
    carousel.addEventListener('mouseenter', () => { hoverPaused = true; scheduleAutoplay(); });
    carousel.addEventListener('mouseleave', () => { hoverPaused = false; scheduleAutoplay(); });
    carousel.addEventListener('focusin', () => { focusPaused = true; scheduleAutoplay(); });
    carousel.addEventListener('focusout', () => {
      window.setTimeout(() => {
        focusPaused = carousel.contains(document.activeElement);
        scheduleAutoplay();
      }, 0);
    });
    document.addEventListener('visibilitychange', scheduleAutoplay);
    reducedMotion.addEventListener('change', (event) => {
      if (event.matches) manuallyPaused = true;
      scheduleAutoplay();
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        outOfView = !entries[0].isIntersecting;
        scheduleAutoplay();
      }, { threshold: .25 });
      observer.observe(carousel);
    } else {
      outOfView = false;
    }

    render(true);
    scheduleAutoplay();
  };

  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-sh-product-carousel]').forEach(initCarousel);
  };

  initCarousels();

  const quickBuys = [];

  const initQuickBuys = (root = document) => {
    root.querySelectorAll('[data-sh-quick-buy]').forEach((quickBuy) => {
      if (quickBuy.dataset.shQuickBuyInitialized === 'true') return;
      quickBuy.dataset.shQuickBuyInitialized = 'true';
      quickBuys.push(quickBuy);
      const variantInput = quickBuy.querySelector('[data-sh-variant-input]');
      const variantButtons = [...quickBuy.querySelectorAll('[data-sh-variant-id]')];
      const addButton = quickBuy.querySelector('[data-sh-add-to-cart]');
      const addLabel = quickBuy.querySelector('[data-sh-add-label]');
      const message = quickBuy.querySelector('[data-sh-quick-buy-message]');

      variantButtons.forEach((button) => {
        button.addEventListener('click', () => {
          variantInput.value = button.dataset.shVariantId;
          variantInput.disabled = false;
          variantButtons.forEach((option) => {
            const selected = option === button;
            option.classList.toggle('is-selected', selected);
            option.setAttribute('aria-pressed', String(selected));
          });
          addButton.disabled = false;
          addButton.classList.remove('is-added');
          addLabel.textContent = 'Add to cart';
          message.textContent = `${button.textContent.trim()} selected.`;
        });
      });

      quickBuy.querySelector('form')?.addEventListener('submit', () => {
        if (!variantInput.value) return;
        addLabel.textContent = 'Adding…';
        message.textContent = 'Adding this item to your cart.';
      });
    });
  };

  initQuickBuys();

  const initCountdowns = (root = document) => {
    root.querySelectorAll('[data-sh-countdown]').forEach((countdown) => {
      if (countdown.dataset.shCountdownInitialized === 'true') return;
      countdown.dataset.shCountdownInitialized = 'true';

      const target = Date.parse(countdown.dataset.countdownTarget || '');
      const complete = countdown.querySelector('[data-countdown-complete]');
      const grid = countdown.querySelector('.sh-release-countdown__grid');
      const status = countdown.querySelector('[data-countdown-status]');
      const units = {
        days: countdown.querySelector('[data-countdown-days]'),
        hours: countdown.querySelector('[data-countdown-hours]'),
        minutes: countdown.querySelector('[data-countdown-minutes]'),
        seconds: countdown.querySelector('[data-countdown-seconds]'),
      };
      const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

      const finish = (label) => {
        grid.hidden = true;
        complete.hidden = false;
        complete.textContent = label;
        status.textContent = label;
        countdown.classList.add('is-complete');
        window.clearInterval(countdown.shCountdownTimer);
      };

      if (!Number.isFinite(target)) {
        finish('Release date to be announced.');
        return;
      }

      const tick = () => {
        const difference = target - Date.now();
        if (difference <= 0) {
          finish(countdown.dataset.completeLabel || 'The release is live.');
          return;
        }

        units.days.textContent = String(Math.floor(difference / 86400000)).padStart(2, '0');
        units.hours.textContent = pad(Math.floor((difference % 86400000) / 3600000));
        units.minutes.textContent = pad(Math.floor((difference % 3600000) / 60000));
        units.seconds.textContent = pad(Math.floor((difference % 60000) / 1000));
      };

      tick();
      countdown.shCountdownTimer = window.setInterval(tick, 1000);
    });
  };

  initCountdowns();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      quickBuys.forEach((quickBuy) => {
        const variantInput = quickBuy.querySelector('[data-sh-variant-input]');
        if (String(variantInput?.value) !== String(event.productVariantId)) return;
        const addButton = quickBuy.querySelector('[data-sh-add-to-cart]');
        const addLabel = quickBuy.querySelector('[data-sh-add-label]');
        const message = quickBuy.querySelector('[data-sh-quick-buy-message]');
        addButton.classList.add('is-added');
        addLabel.textContent = 'Added to cart';
        message.textContent = 'Item added to your cart.';
      });
    });

    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (event.source !== 'product-form') return;
      quickBuys.forEach((quickBuy) => {
        const variantInput = quickBuy.querySelector('[data-sh-variant-input]');
        if (String(variantInput?.value) !== String(event.productVariantId)) return;
        quickBuy.querySelector('[data-sh-add-label]').textContent = 'Try again';
        quickBuy.querySelector('[data-sh-quick-buy-message]').textContent = event.message || 'We could not add this item. Please try again.';
      });
    });
  }

  const stickyPurchase = document.querySelector('[data-sh-sticky-purchase]');

  if (stickyPurchase) {
    const targetId = stickyPurchase.querySelector('[data-sh-sticky-add]')?.dataset.target;
    const stickyButton = stickyPurchase.querySelector('[data-sh-sticky-add]');
    const stickyPrice = stickyPurchase.querySelector('[id^="ShStickyPrice-"]');
    let nativeButtonVisible = true;

    const getNativeButton = () => document.getElementById(targetId);

    const syncStickyPurchase = () => {
      const nativeButton = getNativeButton();
      if (!nativeButton) return;
      const nativeLabel = nativeButton.querySelector('span')?.textContent.trim();
      stickyButton.textContent = nativeLabel || window.variantStrings?.addToCart || 'Add to cart';
      stickyButton.setAttribute('aria-disabled', String(nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true'));
      const currentPrice = document.querySelector('[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular');
      if (currentPrice && stickyPrice) stickyPrice.textContent = currentPrice.textContent.trim();
    };

    const updateStickyVisibility = () => {
      stickyPurchase.classList.toggle('is-visible', !nativeButtonVisible && window.scrollY > 120);
    };

    const nativeButton = getNativeButton();
    if (nativeButton && 'IntersectionObserver' in window) {
      const buttonObserver = new IntersectionObserver((entries) => {
        nativeButtonVisible = entries[0].isIntersecting;
        updateStickyVisibility();
      }, { threshold: .2 });
      buttonObserver.observe(nativeButton);
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    stickyButton.addEventListener('click', () => {
      const liveButton = getNativeButton();
      if (!liveButton) return;
      if (liveButton.disabled || liveButton.getAttribute('aria-disabled') === 'true') {
        const firstOption = document.querySelector('.product-form__input input:not(:disabled) + label');
        firstOption?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      liveButton.click();
    });

    syncStickyPurchase();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(syncStickyPurchase));
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => requestAnimationFrame(syncStickyPurchase));
      subscribe(PUB_SUB_EVENTS.cartError, () => requestAnimationFrame(syncStickyPurchase));
    }
  }

  const mountBucksCurrency = () => {
    const mount = document.querySelector('[data-sh-bucks-footer-mount]');
    const currency = document.querySelector('.buckscc-floating-box');
    if (!mount || !currency) return;
    if (!mount.contains(currency)) mount.append(currency);
    mount.closest('[data-sh-bucks-footer-container]')?.removeAttribute('hidden');
  };

  mountBucksCurrency();

  const bucksCurrencyObserver = new MutationObserver(mountBucksCurrency);
  bucksCurrencyObserver.observe(document.body, { childList: true });

  const footerBreakpoint = window.matchMedia('(max-width: 749px)');
  const footerDetails = [...document.querySelectorAll('[data-sh-footer-details]')];

  const syncFooterDetails = () => {
    footerDetails.forEach((details) => {
      details.open = !footerBreakpoint.matches;
    });
  };

  if (footerDetails.length) {
    syncFooterDetails();
    footerBreakpoint.addEventListener('change', syncFooterDetails);
  }

  document.querySelectorAll('#menu-drawer a').forEach((link) => {
    link.addEventListener('click', () => {
      document.getElementById('Details-menu-drawer-container')?.removeAttribute('open');
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initCarousels(event.target);
    initQuickBuys(event.target);
    initCountdowns(event.target);
    mountBucksCurrency();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const bucksCurrency = event.target.querySelector('[data-sh-bucks-footer-mount] .buckscc-floating-box');
    if (bucksCurrency) document.body.append(bucksCurrency);
    event.target.querySelectorAll('[data-sh-countdown]').forEach((countdown) => {
      window.clearInterval(countdown.shCountdownTimer);
    });
  });
})();
